import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MainPage } from "@/pages/MainPage.js";
import { TestPage } from "@/pages/TestPage.js";
import { TestResultPage } from "@/pages/TestResultPage.js";
import { SurveyPage } from "@/pages/SurveyPage.js";
import { SurveyDonePage } from "@/pages/SurveyDonePage.js";
import { VerifyEmailPage } from "@/pages/VerifyEmailPage.js";
import { TemplestayLandingPage } from "@/pages/TemplestayLandingPage.js";
import { useTenantContext } from "@/hooks/useTenantContext.js";
import { useLangStore } from "@/store/lang.js";

/** Splash DOM 정리: CSS 애니메이션이 끝난 뒤 요소를 제거합니다. */
function useSplashCleanup() {
  useEffect(() => {
    const id = setTimeout(() => {
      document.getElementById("splash")?.remove();
    }, 1700);
    return () => clearTimeout(id);
  }, []);
}

function GlobalSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <svg
        className="h-8 w-8 animate-spin text-stone-400"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
      </svg>
    </div>
  );
}

export function App() {
  const location = useLocation();
  useSplashCleanup();
  const { tenantId, tenantTheme, mainSiteConfig, isLoading } = useTenantContext();
  const { lang } = useLangStore();

  useEffect(() => {
    // favicon은 index.html의 /api/branding/favicon이 처음부터 서빙 — JS 조작 없음
    const titleMap = tenantId ? tenantTheme?.html_title : mainSiteConfig?.html_title;
    if (titleMap) {
      document.title = titleMap[lang] || titleMap.ko || 'Templestay';
    } else if (!tenantId) {
      document.title = 'Templestay';
    }
  }, [tenantId, tenantTheme?.html_title, mainSiteConfig?.html_title, lang]);

  if (isLoading) return <GlobalSpinner />;

  // 메인 모드: templestay.app (tenantId === null)
  if (tenantId === null) {
    return <TemplestayLandingPage />;
  }

  // 사찰 모드: bongeunsa.templestay.app 등 (tenantId !== null)
  return (
    /* aurora-bg에 will-change: filter 없음 → fixed 모달이 viewport 기준으로 정확히 위치됨 */
    <div className="aurora-bg min-h-screen">
      <div className="mx-auto max-w-md min-h-screen">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeInOut" }}
          >
            <Routes location={location}>
              <Route path="/" element={<MainPage />} />
              <Route path="/t/:slug" element={<TestPage />} />
              <Route path="/t/:slug/r/:resultSlug" element={<TestResultPage />} />
              <Route path="/s/:slug" element={<SurveyPage />} />
              <Route path="/s/:slug/done" element={<SurveyDonePage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
